import { useEffect, useState } from "react";
import { X, Share, Plus, Download } from "lucide-react";

const DISMISS_KEY = "cs_install_dismissed_at";
const DISMISS_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function recentlyDismissed() {
  const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return t && Date.now() - t < DISMISS_MS;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState("android"); // "android" | "ios"

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    if (isIOS()) {
      setMode("ios");
      // Slight delay to avoid flashing on initial load
      const t = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(t);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  }

  async function install() {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch {}
    setDeferred(null);
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      className="fixed left-3 right-3 z-50 cs-fade"
      style={{ bottom: "max(12px, env(safe-area-inset-bottom))" }}
      data-testid="install-prompt"
    >
      <div
        className="cs-card mx-auto p-4 flex items-start gap-3"
        style={{ maxWidth: 440, boxShadow: "0 12px 32px rgba(0,0,0,0.12)" }}
      >
        <div
          className="flex items-center justify-center text-white font-semibold flex-shrink-0"
          style={{ width: 40, height: 40, borderRadius: 12, background: "var(--cs-primary)", fontSize: 14 }}
        >CS</div>
        <div className="flex-1 min-w-0">
          {mode === "android" ? (
            <>
              <div className="font-semibold text-[14px]" style={{ letterSpacing: "-0.2px" }}>
                Installer ContracepSur
              </div>
              <div className="text-[12px] mt-0.5" style={{ color: "var(--cs-text-muted)" }}>
                Accès rapide depuis votre écran d'accueil — fonctionne hors-ligne.
              </div>
              <button
                onClick={install}
                className="cs-btn-primary mt-3 text-[13px] py-2 px-3.5"
                data-testid="install-button"
              >
                <Download size={14} /> Installer
              </button>
            </>
          ) : (
            <>
              <div className="font-semibold text-[14px]" style={{ letterSpacing: "-0.2px" }}>
                Ajouter à l'écran d'accueil
              </div>
              <div className="text-[12px] mt-1" style={{ color: "var(--cs-text-muted)" }}>
                Dans Safari : appuyez sur <Share size={12} className="inline -mt-0.5" /> Partager, puis sur{" "}
                <Plus size={12} className="inline -mt-0.5" /> « Sur l'écran d'accueil ».
              </div>
            </>
          )}
        </div>
        <button
          onClick={dismiss}
          className="p-1 rounded-full hover:bg-[var(--cs-bg)] flex-shrink-0"
          aria-label="Fermer"
          data-testid="install-dismiss"
        >
          <X size={16} color="var(--cs-text-muted)" />
        </button>
      </div>
    </div>
  );
}
